<template>
  <div class="wrapper candidates-page">
    <!-- Enhanced Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-title">
          <h1 class="title">Financing Candidates</h1>
          <p class="subtitle">Select a financing candidate to start analyzing their financial documents and chat about their data.</p>
        </div>
        
        <div class="header-actions">
          <button class="landing-btn primary" @click="openAddCandidateForm">
            <span class="btn-icon">➕</span> Add New Candidate
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <div class="content-wrapper">
        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="!loading && candidates.length === 0"
        >
          <i class="las la-user la-5x text-muted mb-3"></i>
          <div class="empty-title">
            No financing candidates found
          </div>
          <div class="empty-subtitle">
            Click "Add New Candidate" to get started.
          </div>
        </div>

        <!-- Error State -->
        <div
          class="empty-state text-center"
          v-if="error"
        >
          <i class="las la-exclamation-triangle la-5x text-danger mb-3"></i>
          <div class="empty-title text-danger">
            {{ error }}
          </div>
          <button 
            class="btn btn-primary btn-sm mt-3" 
            @click="loadCandidates"
          >
            <i class="las la-redo mr-1"></i>
            Try Again
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state text-center">
          <i class="las la-spinner la-spin la-3x text-muted mb-3"></i>
          <div class="empty-title">Loading candidates...</div>
        </div>

        <!-- Enhanced Candidates Grid -->
        <div v-if="!loading && !error && candidates.length > 0" class="candidates-grid">
          <!-- Grid Header -->
          <div class="grid-header">
            <div class="header-row">
              <div class="header-cell candidate-col">
                <i class="las la-user"></i>
                <span>Candidate</span>
              </div>
              <div class="header-cell email-col">
                <i class="las la-envelope"></i>
                <span>Email</span>
              </div>
              <div class="header-cell phone-col">
                <i class="las la-phone"></i>
                <span>Phone</span>
              </div>
              <div class="header-cell notes-col">
                <i class="las la-sticky-note"></i>
                <span>Notes</span>
              </div>
            </div>
          </div>

          <!-- Grid Body -->
          <div class="grid-body">
            <div 
              v-for="candidate in candidates" 
              :key="candidate.id"
              class="candidate-card"
              @click="selectCandidate(candidate)"
            >
              <div class="candidate-cell candidate-col">
                <div class="candidate-info">
                  <div class="candidate-avatar">
                    <div class="avatar-placeholder">
                      <span class="avatar-initials">{{ getInitials(candidate.name) }}</span>
                    </div>
                  </div>
                  <div class="candidate-details">
                    <div class="candidate-name">{{ candidate.name || 'N/A' }}</div>
                  </div>
                </div>
              </div>
              
              <div class="candidate-cell email-col" data-label="Email">
                <div class="email-info">
                  <a :href="`mailto:${candidate.email}`" class="email-link" @click.stop>
                    {{ candidate.email || 'N/A' }}
                  </a>
                </div>
              </div>
              
              <div class="candidate-cell phone-col" data-label="Phone">
                <div class="phone-info">
                  <span class="phone-number">{{ candidate.phone || 'N/A' }}</span>
                </div>
              </div>
              
              <div class="candidate-cell notes-col" data-label="Notes">
                <div class="notes-info">
                  <span class="notes-text" :title="candidate.notes">{{ candidate.notes || 'N/A' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Financing Candidate Form Modal -->
    <FinancingCandidateForm 
      ref="candidateForm"
      @candidate-added="onCandidateAdded"
    />
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import FinancingCandidateForm from "./FinancingCandidateForm.vue";

export default {
  name: 'ChatbotLanding',
  components: {
    FinancingCandidateForm
  },
  data() {
    return {
      candidates: [],
      loading: false,
      error: null
    }
  },
  async mounted() {
    await this.loadCandidates();
  },
  methods: {
    async loadCandidates() {
      this.loading = true;
      try {
        const data = await ChatbotService.getFinancingCandidates();
        this.candidates = Array.isArray(data) ? data : (data.candidates || data.data || []);
      } catch (err) {
        this.error = 'Failed to load financing candidates.';
        console.error('Error loading candidates:', err);
      } finally {
        this.loading = false;
      }
    },
    
    selectCandidate(candidate) {
      this.$router.push({ 
        name: 'chatbot-candidate-sessions', 
        params: { 
          candidateId: candidate.id
        }
      });
    },
    
    openAddCandidateForm() {
      this.$refs.candidateForm.open();
    },
    
    onCandidateAdded(candidate) {
      // Refresh the candidates list
      this.loadCandidates();
      
      // Show success message
      this.$bvToast.toast('Financing candidate added successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000
      });
    },
    
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

// Main wrapper styling with modern gradient background
.wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 1rem;
  overflow-x: hidden;
  max-width: 100%;
}

// Modern page header design
.page-header {
  background: linear-gradient(135deg, #212529 0%, #495057 100%);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(33, 37, 41, 0.3);
  color: white;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .header-title {
    flex: 1;
    
    .title {
      font-size: 2rem;
      font-weight: 700;
      margin: 0;
      margin-bottom: 0.125rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    .subtitle {
      font-size: 1rem;
      opacity: 0.9;
      margin: 0;
      font-weight: 300;
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
}

.landing-btn {
  font-family: $brand-font-medium;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  
  &.primary {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 2px 12px rgba(115, 243, 169, 0.3);
    
    &:hover {
      background: adjust-color($color-primary, $lightness: -5%);
      box-shadow: 0 4px 20px rgba(115, 243, 169, 0.4);
      transform: translateY(-2px);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
}

.btn-icon {
  font-size: 1.1rem;
}

// Main content area
.main-content {
  .content-wrapper {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
}

// Enhanced candidates grid
.candidates-grid {
  .grid-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 0.75rem 0;
    border-bottom: 1px solid #dee2e6;
    position: sticky;
    top: 0;
    z-index: 10;
    
    .header-row {
      display: grid;
      grid-template-columns: 2.5fr 1.8fr 1.2fr 1.5fr;
      gap: 0.75rem;
      padding: 0 1rem;
      align-items: center;
    }
    
    .header-cell {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.9rem;
      font-weight: 700;
      color: #495057;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      i {
        font-size: 0.9rem;
        color: #212529;
      }
    }
  }
  
  .grid-body {
    padding: 0.5rem;
    
    .candidate-card {
      display: grid;
      grid-template-columns: 2.5fr 1.8fr 1.2fr 1.5fr;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      cursor: pointer;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, $color-primary, lighten($color-primary, 20%));
        transform: scaleX(0);
        transition: transform 0.3s ease;
        transform-origin: left;
      }
      
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(33, 37, 41, 0.15);
        border-color: $color-primary;
        
        &::before {
          transform: scaleX(1);
        }
      }
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    .candidate-cell {
      display: flex;
      align-items: center;
      min-height: 1.5rem;
      overflow: hidden;
      padding: 0.125rem 0;
    }
  }
}

// Enhanced candidate info with avatar
.candidate-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  
  .candidate-avatar {
    width: 32px;
    height: 32px;
    border-radius: 0.5rem;
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    
    .avatar-placeholder {
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, $color-primary, darken($color-primary, 15%));
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.375rem;
      
      .avatar-initials {
        color: $color-secondary;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
      }
    }
  }
  
  .candidate-details {
    min-width: 0;
    flex: 1;
    
    .candidate-name {
      font-weight: 700;
      font-size: 1rem;
      color: #1e293b;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

// Enhanced email info
.email-info {
  width: 100%;
  
  .email-link {
    color: #212529;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0.2rem 0.2rem;
    margin-top: 0.2rem;
    border-radius: 0.375rem;
    background: rgba(33, 37, 41, 0.1);
    display: inline-block;
    max-width: 100%;
    
    &:hover {
      background: rgba(33, 37, 41, 0.2);
      color: #000000;
      text-decoration: none;
      transform: scale(1.05);
    }
  }
}

// Phone and notes info
.phone-info, .notes-info {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  
  .phone-number, .notes-text {
    font-size: 0.8rem;
    color: #212529;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .notes-text {
    font-style: italic;
    color: #6c757d;
  }
}

// Enhanced empty states
.empty-state, .loading-state {
  padding: 2.5rem 1.5rem;
  text-align: center;
  
  i {
    opacity: 0.4;
    margin-bottom: 1rem;
    color: #6c757d;
    
    &.la-spin {
      animation: spin 1s linear infinite;
    }
  }
  
  .empty-title {
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
    color: #212529;
  }
  
  .empty-subtitle {
    font-size: 0.9rem;
    opacity: 0.8;
    color: #495057;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Responsive design
@media (max-width: 1200px) {
  .candidates-grid {
    .grid-header .header-row,
    .grid-body .candidate-card {
      grid-template-columns: 2.2fr 1.6fr 1.1fr 1.3fr;
      gap: 1.25rem;
    }
  }
}

@media (max-width: 992px) {
  .wrapper {
    padding: 0.75rem;
  }
  
  .page-header {
    padding: 1rem;
    
    .header-content {
      flex-direction: column;
      gap: 1rem;
    }
    
    .header-title .title {
      font-size: 1.375rem;
    }
  }
  
  .candidates-grid {
    .grid-header .header-row,
    .grid-body .candidate-card {
      grid-template-columns: 2.5fr 1.8fr 1.2fr 1.5fr;
      gap: 1rem;
      
      .notes-col {
        display: none;
      }
    }
  }
}

@media (max-width: 768px) {
  .wrapper {
    padding: 0.75rem;
  }
  
  .page-header {
    padding: 1rem;
    margin-bottom: 1rem;
    
    .header-title .title {
      font-size: 1.25rem;
    }
    
    .header-title .subtitle {
      font-size: 0.8rem;
    }
  }
  
  .candidates-grid {
    .grid-header {
      display: none;
    }
    
    .grid-body {
      padding: 0.5rem;
      
      .candidate-card {
        grid-template-columns: 1fr;
        gap: 1rem;
        padding: 1.5rem;
        
        .candidate-cell {
          width: 100%;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f8f9fa;
          
          &:last-child {
            border-bottom: none;
          }
          
          &:before {
            content: attr(data-label);
            font-weight: 700;
            font-size: 0.75rem;
            color: #212529;
            text-transform: uppercase;
            margin-right: 1rem;
            min-width: 80px;
          }
          
          &.candidate-col {
            justify-content: flex-start;
            border-bottom: 2px solid #f8f9fa;
            margin-bottom: 0.5rem;
            
            &:before {
              display: none;
            }
          }
        }
        
        .candidate-info {
          width: 100%;
          margin-bottom: 0.5rem;
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .wrapper {
    padding: 0.5rem;
  }
  
  .page-header {
    padding: 0.875rem;
    
    .header-title .title {
      font-size: 1.125rem;
    }
  }
  
  .candidate-info {
    .candidate-avatar {
      width: 28px;
      height: 28px;
    }
    
    .candidate-details .candidate-name {
      font-size: 0.8rem;
    }
  }
}
</style> 